import dayjs from "dayjs"
import { type TFunction } from "i18next"

import type {
  AudioAssetView,
  EditionStatus,
  EditionView,
  EducationCreatedBy,
  EngagementCustomerView,
  EngagementSalesPersonView,
  TrackView
} from "./types/education.types.ts"
import type { AudioResult } from "../../../../components/Education/EducationAudioPlayer.tsx"
import { READABLE_EDITION_STATUSES } from "./education-contract.ts"

export { READABLE_EDITION_STATUSES }

/**
 * The edition selector's default: the newest edition with real content, or —
 * if nothing has generated yet (every edition still `pending`/`failed`) —
 * the newest edition at all, so the reader lands on its pending/failed state
 * rather than nothing.
 */
export const defaultEditionId = (editions: EditionView[]): string | null => {
  if (editions.length === 0) return null
  const readable = editions.filter((edition) =>
    READABLE_EDITION_STATUSES.includes(edition.status)
  )
  const pool = readable.length > 0 ? readable : editions
  return pool[pool.length - 1].id
}

export type InFlightTrackStatus = "generating" | "ready" | "failed"

const FAILED_EDITION_STATUSES: EditionStatus[] = ["failed", "blocked"]

/** Per-track progress for the "Regenerating…" banner — the in-flight
 * package's own track, simplified to the same three states Task 3's
 * placeholder used for the pre-generation view. */
export const simplifyInFlightStatus = (
  track: TrackView
): InFlightTrackStatus => {
  const latest = track.editions[track.editions.length - 1]
  if (!latest) return "generating"
  if (FAILED_EDITION_STATUSES.includes(latest.status)) return "failed"
  if (READABLE_EDITION_STATUSES.includes(latest.status)) return "ready"
  return "generating"
}

/** The account's own date format, resolved once at the `EducationTrackView`
 * level via `useDateFormat()` and passed down as a plain string — this
 * helper stays a pure function (no hook, no Redux) so `EditionHeader` and
 * `EditionTimeline` can be unit-tested by simply passing a format string,
 * the same way the rest of this app formats dates in the account's own
 * preference (`userFormat || DATE_FORMAT`, see e.g.
 * `ProjectViewMilestones/columns.tsx`). */
export const formatEditionDate = (iso: string, format: string): string =>
  dayjs(iso).format(format)

export const describeCreatedBy = (
  createdBy: EducationCreatedBy,
  t: TFunction
): string => {
  if (createdBy.type === "customer") {
    return createdBy.name
      ? t("projectEducation.trackView.generatedByCustomer", {
          name: createdBy.name
        })
      : t("projectEducation.trackView.generatedByCustomerAnonymous")
  }
  if (createdBy.type === "system" || !createdBy.name) {
    return t("projectEducation.trackView.generatedBySystem")
  }
  return t("projectEducation.trackView.generatedBy", { name: createdBy.name })
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** "Updated 8 min ago" — a small hand-rolled formatter rather than pulling in
 * dayjs's relativeTime plugin, which nothing else in this app has extended
 * globally (see `src/providers/DayjsProvider.tsx`). Takes `now` explicitly so
 * a test can pin the clock with `vi.setSystemTime` and assert an exact string. */
export const formatUpdatedRelative = (
  iso: string,
  now: Date,
  t: TFunction
): string => {
  const then = new Date(iso).getTime()
  const diffMs = Math.max(0, now.getTime() - then)

  if (diffMs < MINUTE_MS) return t("projectEducation.trackView.updatedJustNow")
  if (diffMs < HOUR_MS) {
    return t("projectEducation.trackView.updatedMinutesAgo", {
      count: Math.floor(diffMs / MINUTE_MS)
    })
  }
  if (diffMs < DAY_MS) {
    return t("projectEducation.trackView.updatedHoursAgo", {
      count: Math.floor(diffMs / HOUR_MS)
    })
  }
  return t("projectEducation.trackView.updatedDaysAgo", {
    count: Math.floor(diffMs / DAY_MS)
  })
}

/** Strips every `<sup>` citation marker before reading text, and prefers the
 * backend's own `<p class="summary">` paragraph (see
 * `EducationArticle.tsx`'s sanitizer comment) over guessing at structure —
 * falling back to the first paragraph for an edition whose render predates
 * that convention. */
export const extractSummaryText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html")
  doc.querySelectorAll("sup").forEach((node) => node.remove())
  const summaryEl = doc.querySelector("p.summary") ?? doc.querySelector("p")
  return (summaryEl?.textContent ?? "").trim().replace(/\s+/g, " ")
}

/** The audio player's transcript — every paragraph/list item/heading's text,
 * citation markers stripped and the source list dropped, joined back into
 * readable prose. Distinct from `extractSummaryText`, which deliberately
 * keeps to just the lead paragraph. */
export const extractPlainText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html")
  doc.querySelectorAll("sup").forEach((node) => node.remove())
  doc.querySelectorAll(".section-citations").forEach((node) => node.remove())
  const blocks = Array.from(doc.body.querySelectorAll("h2, h3, p, li"))
  const text = blocks
    .map((el) => el.textContent?.trim() ?? "")
    .filter(Boolean)
    .map((block, index, all) =>
      index === all.length - 1 || /[.!?:]$/.test(block) ? block : `${block}.`
    )
    .join(" ")
  return (text || doc.body.textContent?.trim() || "")
    .replace(/\s+/g, " ")
    .trim()
}

/** "Copy summary" — title, summary paragraph, next actions, plain text, no
 * citation markers and no source list. */
export const buildCopySummaryText = (
  trackTitle: string,
  edition: EditionView,
  t: TFunction
): string => {
  const parts = [
    `${trackTitle} — ${editionLabel(edition, t)}`,
    extractSummaryText(edition.html)
  ]
  if (edition.next_actions.length > 0) {
    parts.push(
      [
        t("projectEducation.trackView.nextActionsHeading"),
        ...edition.next_actions.map((action) => `- ${action}`)
      ].join("\n")
    )
  }
  return parts.filter(Boolean).join("\n\n")
}

/**
 * The History timeline's per-edition "has the sales person read/listened"
 * status (product owner request, 2026-09-25). `completed` outranks
 * `listened_pct` — a listener who finished at, say, 80% (skipped the last
 * bit) still reads as done, not as a stalled-out percentage.
 */
export const salesPersonEngagementLabel = (
  engagement: EngagementSalesPersonView,
  dateFormat: string,
  t: TFunction
): string => {
  if (engagement.completed) {
    return t("projectEducation.editionTimeline.engagement.salesListened")
  }
  if (engagement.listened_pct != null && engagement.listened_pct > 0) {
    return t("projectEducation.editionTimeline.engagement.salesListening", {
      pct: engagement.listened_pct
    })
  }
  if (engagement.viewed_at) {
    return t("projectEducation.editionTimeline.engagement.salesRead", {
      date: formatEditionDate(engagement.viewed_at, dateFormat)
    })
  }
  return t("projectEducation.editionTimeline.engagement.salesNotOpened")
}

/** The History timeline's per-edition customer status — `null` engagement
 * (a sales_person-track edition) is the caller's job to skip rendering
 * entirely, not this function's. */
export const customerEngagementLabel = (
  engagement: EngagementCustomerView,
  dateFormat: string,
  t: TFunction
): string => {
  if (engagement.listened_pct != null && engagement.listened_pct > 0) {
    return t("projectEducation.editionTimeline.engagement.customerListening", {
      pct: engagement.listened_pct
    })
  }
  if (engagement.state === "opened") {
    return t("projectEducation.editionTimeline.engagement.customerOpened", {
      date: engagement.opened_at
        ? formatEditionDate(engagement.opened_at, dateFormat)
        : ""
    })
  }
  if (engagement.state === "shared") {
    return t(
      "projectEducation.editionTimeline.engagement.customerSharedNotOpened"
    )
  }
  return t("projectEducation.editionTimeline.engagement.customerNotShared")
}

export type CustomerPublishState =
  | "published"
  | "reviewRequired"
  | "unpublished"

/**
 * Task 4's whole footprint on publishing (Task 6 builds the actual
 * publish/preview flow): a read-only status badge on the customer track.
 * `published_at` wins over a `review_required` status because a
 * customer-initiated More edition can pass safety checks and auto-publish
 * (spec §2, "Customer initiated More") while still carrying a status the
 * backend hasn't rewritten off `review_required` yet.
 */
export const customerPublishState = (
  edition: EditionView
): CustomerPublishState => {
  if (edition.published_at) return "published"
  if (edition.status === "review_required") return "reviewRequired"
  return "unpublished"
}

export const askForMoreLabel = (remaining: number, t: TFunction): string =>
  remaining > 0
    ? t("projectEducation.trackView.askForMore", { count: remaining })
    : t("projectEducation.trackView.maximumDepth")

/** Base / Section 2 / Section 3, translated from the edition's number; the
 *  backend's English label is the fallback for a number with no key. */
export const editionLabel = (
  edition: Pick<EditionView, "number" | "label">,
  t: TFunction
): string =>
  t(`projectEducation.editionLabels.${edition.number}`, {
    defaultValue: edition.label
  })

export const editionStatusLabel = (
  edition: Pick<EditionView, "status" | "status_label">,
  t: TFunction
): string =>
  t(`projectEducation.editionStatus.${edition.status}`, {
    defaultValue: edition.status_label
  })

// ezbuild_back `snapshot.FIELD_LABELS`: `stale_fields` arrives as these
// English labels.
const STALE_FIELD_KEYS: Record<string, string> = {
  "Project type": "project_type",
  "Project description": "project_description",
  "Scope notes": "detailed_project_description",
  Timeline: "timeline",
  Budget: "budget_range",
  Location: "location",
  "Property ownership": "property_ownership",
  "Needs permit": "needs_permit",
  "Needs design": "needs_design",
  "Supply purchase responsibility": "supply_purchase_responsibility",
  "Customer name": "customer_display_name",
  "Internal notes": "internal_notes",
  "Lead source": "lead_source"
}

export const staleFieldLabel = (label: string, t: TFunction): string => {
  const key = STALE_FIELD_KEYS[label]
  return key
    ? t(`projectEducation.staleFields.${key}`, { defaultValue: label })
    : label
}

const OTHER_PROJECT_TYPE = "other"

/** The customer-facing project name, derived the way ezbuild_back's
 *  `public_serializers._project_label` does. */
export const customerProjectLabel = (
  projectTypeName: string | null | undefined,
  t: TFunction
): string => {
  const name = (projectTypeName ?? "").trim()
  if (!name || name.toLowerCase() === OTHER_PROJECT_TYPE) {
    return t("projectEducation.customerPreviewModal.yourProject")
  }
  return name
}

/** `getAudio`'s enveloped `{details:{data}}` on 200/202, unwrapped into the
 * shape `EducationAudioPlayer` expects. */
export const toAudioResult = (
  asset: AudioAssetView | null | undefined
): AudioResult => {
  if (asset?.status === "ready" && asset.url && asset.duration_ms != null) {
    return { status: "ready", url: asset.url, duration_ms: asset.duration_ms }
  }
  if (asset?.status === "queued" || asset?.status === "synthesizing") {
    return { status: asset.status }
  }
  return { status: "failed" }
}
